// The crosstable view on the Standings page, and the printed version of it.
//
// The shape of the table and the rules for filling it live in js/utils/crosstable.js; this
// file turns what that returns into HTML. The tab that shows it is owned by
// js/ui/standings-views.js, which this module registers with at the bottom.
//
// The ranking comes from window.TieBreak.compute with the same arguments renderStandings()
// uses, so a player is in the same place in both tables and a cell that says "12w1" sends
// the reader to the row the standings also call 12.

import { state, getActiveSettings, countedRoundsFor } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';
import { buildCrosstable, preferredKind } from '../utils/crosstable.js';
import { registerStandingsView } from './standings-views.js';

const ROUND_ROBIN_FORMATS = [
    'rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'
];

/** The ranked order the standings screen produced, for the same tournament settings. */
function rankedOrder() {
    const settings = getActiveSettings();
    const isRR = ROUND_ROBIN_FORMATS.includes(settings.format);
    return window.TieBreak.compute(state.players, state.rounds, {
        pointSystem: settings.pointSystem,
        tiebreaks: settings.tiebreaks,
        totalRounds: parseInt(settings.roundsCount, 10) || state.rounds.length,
        isRoundRobin: isRR,
        countedRounds: countedRoundsFor(settings)
    }).order;
}

/** The crosstable for the active tournament, in whichever shape suits it. */
function currentTable() {
    const settings = getActiveSettings();
    const order = rankedOrder();
    return buildCrosstable({
        players: state.players,
        rounds: state.rounds,
        order,
        kind: preferredKind({ format: settings.format, playerCount: order.length }),
        totalRounds: parseInt(settings.roundsCount, 10) || state.rounds.length
    });
}

// A result glyph, coloured the way the standings colour an opponent: green won, amber
// drew, red lost. Anything else — a game still in progress, a forfeit — stays neutral.
function charHtml(char) {
    let colour = 'var(--text-main)';
    if (char === '1') colour = 'var(--success)';
    else if (char === '½') colour = 'var(--warning)';
    else if (char === '0') colour = 'var(--danger)';
    return `<span style="color: ${colour}; font-weight: 600;">${escapeHtml(char || '')}</span>`;
}

function nameCellHtml(row) {
    const player = state.players.find(p => String(p.id) === String(row.id));
    return `<td style="max-width: 260px;"><div class="tbl-title clickable-player" data-id="${escapeHtml(row.id)}"
        style="cursor: pointer; color: var(--primary); white-space: normal; word-break: break-word;"
        title="${escapeHtml(formatPlayerName(player, false))}">${formatPlayerName(player, true)}</div></td>`;
}

function ratingOf(row) {
    const player = state.players.find(p => String(p.id) === String(row.id));
    const rating = player && player.rating;
    return (rating && rating !== 0 && rating !== '0') ? String(rating) : '';
}

// ---- round-by-round shape --------------------------------------------------

function roundCellHtml(cell) {
    if (cell.kind === 'none') {
        return `<td style="text-align: center; color: var(--text-muted);">·</td>`;
    }
    if (cell.kind === 'bye') {
        const label = cell.requested ? tr('Requested bye') : tr('Pairing bye');
        return `<td style="text-align: center; white-space: nowrap;" title="${escapeHtml(label)}">
            <span style="color: var(--text-muted);">—</span>${charHtml(cell.char)}</td>`;
    }
    const opponent = cell.opponentRank == null ? '?' : cell.opponentRank;
    return `<td style="text-align: center; white-space: nowrap;">
        <span style="color: var(--text-muted);">${escapeHtml(String(opponent))}${escapeHtml(cell.colour)}</span>${charHtml(cell.char)}</td>`;
}

function renderRoundTable(table, headerRow, body) {
    headerRow.innerHTML = `
        <th style="width: 50px;">${tr('Rank')}</th>
        <th style="max-width: 260px;">${tr('Player')}</th>
        <th style="width: 70px;">${tr('Rating')}</th>
        ${table.columns.map(c => `<th style="width: 54px; text-align: center;">${c.round}</th>`).join('')}
        <th style="width: 70px;">${tr('Points')}</th>`;

    body.innerHTML = table.rows.map(row => `
        <tr data-player-id="${escapeHtml(row.id)}">
            <td><strong>${row.rank}</strong></td>
            ${nameCellHtml(row)}
            <td>${escapeHtml(ratingOf(row))}</td>
            ${row.cells.map(roundCellHtml).join('')}
            <td><span class="points-value">${row.points}</span></td>
        </tr>`).join('');
}

// ---- grid shape ------------------------------------------------------------

function gridCellHtml(cell, isSelf) {
    if (isSelf) {
        return `<td style="text-align: center; background: var(--border-color); opacity: 0.5;"></td>`;
    }
    if (cell.kind !== 'game' || cell.games.length === 0) {
        return `<td style="text-align: center; color: var(--text-muted);">·</td>`;
    }
    // A double round robin puts two games in one cell; they are shown in round order,
    // which is the only ordering that tells the reader which was which.
    const inner = cell.games
        .map(g => `<span title="${escapeHtml(tr('Round {n}', { n: g.round }))}">${charHtml(g.char)}</span>`)
        .join('<span style="color: var(--text-muted);">/</span>');
    return `<td style="text-align: center; white-space: nowrap;">${inner}</td>`;
}

function renderGridTable(table, headerRow, body) {
    headerRow.innerHTML = `
        <th style="width: 50px;">${tr('Rank')}</th>
        <th style="max-width: 260px;">${tr('Player')}</th>
        <th style="width: 70px;">${tr('Rating')}</th>
        ${table.columns.map(c => `<th style="width: 40px; text-align: center;">${c.rank}</th>`).join('')}
        <th style="width: 70px;">${tr('Points')}</th>`;

    body.innerHTML = table.rows.map(row => `
        <tr data-player-id="${escapeHtml(row.id)}">
            <td><strong>${row.rank}</strong></td>
            ${nameCellHtml(row)}
            <td>${escapeHtml(ratingOf(row))}</td>
            ${row.cells.map((cell, i) => gridCellHtml(cell, table.columns[i].rank === row.rank)).join('')}
            <td><span class="points-value">${row.points}</span></td>
        </tr>`).join('');
}

// ---- view ------------------------------------------------------------------

export function renderCrosstable() {
    const headerRow = document.getElementById('crosstable-header-row');
    const body = document.getElementById('crosstable-body');
    const legend = document.getElementById('crosstable-legend');
    if (!headerRow || !body) return;

    const table = currentTable();

    if (table.rows.length === 0) {
        headerRow.innerHTML = '';
        body.innerHTML = `<tr><td style="text-align: center; padding: 3rem; color: var(--text-muted);">${tr('No results recorded yet.')}</td></tr>`;
        if (legend) legend.textContent = '';
        return;
    }

    if (table.kind === 'grid') renderGridTable(table, headerRow, body);
    else renderRoundTable(table, headerRow, body);

    // The round-by-round shape needs saying out loud once; a grid does not.
    if (legend) {
        legend.textContent = table.kind === 'rounds'
            ? tr('Each cell is the opponent’s rank, the colour played, and the result — “12w1” means a win with White against rank 12.')
            : '';
    }

    // Names are `.clickable-player`, which js/app.js already opens through one delegated
    // document-level handler. Wiring a second listener here would open the profile twice.
}

// ---- print -----------------------------------------------------------------

/**
 * The crosstable as a printed table.
 *
 * Built from the same buildCrosstable() call as the screen, so the sheet pinned to the
 * wall and the page an arbiter is looking at cannot disagree. Plain characters and the
 * print stylesheet's own classes — no colours, because the result glyphs carry the meaning
 * and most of these come out of a black-and-white printer.
 */
export function crosstablePrintHtml() {
    const table = currentTable();
    if (table.rows.length === 0) {
        return `<p class="text-center" style="padding: 2rem;">${tr('No results recorded yet.')}</p>`;
    }

    const isGrid = table.kind === 'grid';
    const headers = isGrid
        ? table.columns.map(c => `<th style="width: 26pt;" class="text-center">${c.rank}</th>`).join('')
        : table.columns.map(c => `<th style="width: 34pt;" class="text-center">${c.round}</th>`).join('');

    const cellText = (row) => row.cells.map((cell, i) => {
        if (isGrid) {
            if (table.columns[i].rank === row.rank) return `<td class="text-center">&nbsp;</td>`;
            if (cell.kind !== 'game' || cell.games.length === 0) return `<td class="text-center">·</td>`;
            return `<td class="text-center">${escapeHtml(cell.games.map(g => g.char).join('/'))}</td>`;
        }
        if (cell.kind === 'none') return `<td class="text-center">·</td>`;
        if (cell.kind === 'bye') return `<td class="text-center">—${escapeHtml(cell.char)}</td>`;
        const opponent = cell.opponentRank == null ? '?' : cell.opponentRank;
        return `<td class="text-center">${escapeHtml(`${opponent}${cell.colour}${cell.char}`)}</td>`;
    }).join('');

    const rows = table.rows.map(row => {
        const player = state.players.find(p => String(p.id) === String(row.id));
        return `
            <tr>
                <td class="text-center font-bold">${row.rank}</td>
                <td>${escapeHtml(formatPlayerName(player))}</td>
                <td class="text-right">${escapeHtml(ratingOf(row))}</td>
                ${cellText(row)}
                <td class="text-right font-bold">${row.points}</td>
            </tr>`;
    }).join('');

    const legend = isGrid
        ? tr('A cell is the result against the player in that column. Two results separated by a slash are the two games of a double round robin.')
        : tr('Each cell is the opponent’s rank, the colour played, and the result — “12w1” means a win with White against rank 12.');

    return `
        <h2 class="text-center" style="margin-bottom: 1.5rem;">${tr('Crosstable after Round {n}', { n: state.rounds.length })}</h2>
        <table class="print-table">
            <thead>
                <tr>
                    <th style="width: 30pt;" class="text-center">${tr('Rank')}</th>
                    <th>${tr('Player')}</th>
                    <th style="width: 45pt;" class="text-right">${tr('Rating')}</th>
                    ${headers}
                    <th style="width: 40pt;" class="text-right">${tr('Pts')}</th>
                </tr>
            </thead>
            <tbody>${rows}</tbody>
        </table>
        <div style="font-size: 9pt; margin-top: 1rem;"><p>${legend}</p></div>`;
}

// The tab itself belongs to js/ui/standings-views.js, which owns the switch between the
// three views of this page. This module only says which container is the crosstable's,
// which print button goes with it, and what to call to draw it.
registerStandingsView('crosstable', {
    wrapId: 'crosstable-wrap',
    buttonIds: ['btn-print-crosstable'],
    render: renderCrosstable
});
